import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, Watch } from "@stencil/core";
import ToggleButton from "./toggle";

/** The base className used for child elements */
const baseClass = "ux-collapsible";

/** The expand/collapse transition duration in ms */
const transitionDuration = 250;

@Component({
    tag: "ux-collapsible",
    styleUrl: "collapsible.scss",
    shadow: false
})
export class Collapsible implements ComponentInterface {
    @Element() domNode!: HTMLUxCollapsibleElement;

    /** toggle expanded state  */
    @Prop({ reflect: true, mutable: true }) isExpanded: boolean;

    /** the toggle button text in default collapsed state  */
    @Prop() readonly buttonTextCollapsed: string;

    /** direction of toggle opening  */
    @Prop() readonly direction: string = "top";

    /** the toggle button text in expanded state  */
    @Prop() readonly buttonTextExpanded: string;

    /** if set, the toggle button will be in positioned in the middle */
    @Prop() readonly centeredButton: boolean = false;

    /** extender class for button*/
    @Prop() readonly buttonClass: string = "";

    /** if set, the toggle button will look like an accordion and act like a collapsible */
    @Prop() readonly accordion: boolean = false;

    /** the content container html element */
    private contentContainer: HTMLDivElement | null = null;

    /** the content inner html element */
    private contentInner: HTMLDivElement | null = null;

    /** the current transition timeout id */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private transitionTimeoutId: any | null = null;

    /** fired when the collapsible is expanded or collapsed  */
    @Event({ eventName: "uxCollapsibleExpanded" })
    collapsibleExpanded!: EventEmitter<boolean>;

    @Watch("isExpanded")
    setIsExpanded(currentlyExpanded: boolean) {
        if (this.transitionTimeoutId !== null) {
            clearTimeout(this.transitionTimeoutId);
            this.transitionTimeoutId = null;
        }
        // @ts-expect-error Check for typing
        const contentHeight = Math.ceil(this.contentInner.offsetHeight);

        // @ts-expect-error Check for typing
        this.contentContainer.style.height = `${contentHeight}px`;
        // @ts-expect-error Check for typing
        this.contentContainer.style.overflow = "hidden";
        if (currentlyExpanded) {
            this.transitionTimeoutId = setTimeout(() => {
                // @ts-expect-error Check for typing
                this.contentContainer.style.height = "auto";
                // @ts-expect-error Check for typing
                this.contentContainer.style.overflow = "visible";
            }, transitionDuration);
        } else {
            this.transitionTimeoutId = setTimeout(() => {
                // @ts-expect-error Check for typing
                this.contentContainer.style.height = "0";
            }, 10);
        }
    }

    private toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
        this.collapsibleExpanded.emit(this.isExpanded);
    }

    componentDidLoad() {
        this.contentContainer = this.domNode.querySelector(`.${baseClass}-content__container`);
        this.contentInner = this.domNode.querySelector(`.${baseClass}-content__inner`);
    }

    render() {
        const buttonText = this.isExpanded ? this.buttonTextExpanded : this.buttonTextCollapsed;
        const containerClassName = {
            [baseClass]: true,
            [`${baseClass}--expanded`]: this.isExpanded,
            [`${baseClass}--collapsed`]: !this.isExpanded,
            [`${baseClass}--direction-bottom`]: this.direction === "bottom",
            [`${baseClass}--accordion`]: this.accordion
        };
        return (
            <Host>
                <div class={containerClassName}>
                    {this.direction !== "bottom" && (
                        <div class={`${baseClass}-content__container`}>
                            <div class={`${baseClass}-content__inner`} aria-hidden={!this.isExpanded}>
                                <slot />
                            </div>
                        </div>
                    )}
                    <div
                        class={{
                            [`${baseClass}-button__container`]: true,
                            [`${baseClass}-button__container--centered`]: this.centeredButton
                        }}
                    >
                        <ToggleButton
                            baseClass={this.buttonClass !== "" ? this.buttonClass + " " + baseClass : baseClass}
                            label={buttonText}
                            onClick={() => this.toggleExpanded()}
                            accordion={this.accordion}
                        />
                    </div>
                    {this.direction === "bottom" && (
                        <div class={`${baseClass}-content__container`}>
                            <div class={`${baseClass}-content__inner`} aria-hidden={!this.isExpanded}>
                                <slot />
                            </div>
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
